/**
 * Offline / sync banner.
 * Shows connection status AND the offline write outbox: when offline, writes are
 * queued locally and this shows how many are waiting; on reconnect it drains the
 * queue to the server and reports progress. (See src/lib/outbox.ts)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { pendingCount, onOutboxChange, flushOutbox } from '../../lib/outbox';
import './OfflineBanner.css';

const OfflineBanner: React.FC = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [pending, setPending] = useState(pendingCount());
  const [syncing, setSyncing] = useState(false);
  const [justSynced, setJustSynced] = useState(false);

  const drain = useCallback(async () => {
    if (pendingCount() === 0) return;
    setSyncing(true);
    const res = await flushOutbox();
    setSyncing(false);
    if (res.ok > 0 && pendingCount() === 0) {
      setJustSynced(true);
      setTimeout(() => setJustSynced(false), 3000);
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => { setIsOffline(false); drain(); };
    const handleOffline = () => { setIsOffline(true); };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    const unsub = onOutboxChange(() => setPending(pendingCount()));
    // Flush anything left from a previous session if we start online.
    if (navigator.onLine) drain();
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      unsub();
    };
  }, [drain]);

  if (!isOffline && !syncing && !justSynced && pending === 0) return null;

  if (isOffline) {
    return (
      <div className="offline-banner offline">
        <span className="offline-icon">⚠</span>
        <span>
          You're offline.{' '}
          {pending > 0
            ? `${pending} change${pending === 1 ? '' : 's'} saved on this device — they'll sync when you reconnect.`
            : 'Changes will be saved on this device and synced when you reconnect.'}
        </span>
      </div>
    );
  }

  if (syncing) {
    return (
      <div className="offline-banner reconnected">
        <span className="offline-icon">⟳</span>
        <span>Back online — syncing {pending} change{pending === 1 ? '' : 's'}…</span>
      </div>
    );
  }

  if (pending > 0) {
    return (
      <div className="offline-banner offline">
        <span className="offline-icon">⚠</span>
        <span>{pending} change{pending === 1 ? '' : 's'} still pending sync.</span>
        <button className="offline-retry" onClick={drain}>Retry sync</button>
      </div>
    );
  }

  return (
    <div className="offline-banner reconnected">
      <span className="offline-icon">✓</span>
      <span>All changes synced.</span>
    </div>
  );
};

export default OfflineBanner;
